import { addHttpIfMissing, AppIcon, College } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ReactComponent as UniversityLogo } from '@backrs_ui/grow-ui/src/assets/images/university-logo-default.svg';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

const LOGO_SIZE = 160;
const HEADER_IMAGE_HEIGHT = 240;
const NO_BANNER_IMAGE_HEIGHT = 185;

const COLLEGE_HEADER_STRINGS = appStrings.modules.college.header;

type CollegeHeaderProps = Readonly<{
  college: College;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const CollegeHeader = ({ college, handleBookmark, hasBookmarked }: CollegeHeaderProps) => {
  return (
    <Box sx={headerContainerSx}>
      {/* IMAGES */}
      {college.banner_media_url ? (
        <Box component="img" src={college.banner_media_url} sx={largeImageSx} />
      ) : (
        <Box sx={noBannerImageSx} />
      )}

      <Box sx={schoolInfoSx}>
        {college.logo_media_url ? (
          <Box component="img" src={college.logo_media_url} sx={iconImageSx} />
        ) : (
          <Box sx={iconImageSx}>
            <UniversityLogo />
          </Box>
        )}
        {/* SCHOOL NAME AND LOCATION */}
        <Box sx={schoolNameSx}>
          <Typography variant="display-xs-semibold">{college.school_name}</Typography>
          <Typography variant="text-sm-regular">
            {college.city}, {college.state}
          </Typography>
        </Box>

        {/* BUTTONS */}
        <Box sx={buttonContainerSx}>
          {college.url && (
            <Button
              href={addHttpIfMissing(college.url)}
              target="_blank"
              hierarchy="Secondary gray"
              size="md"
              startIcon={
                <AppIcon
                  iconName="LinkExternal01"
                  diameter="20px"
                  color="Colors/Text/text-secondary"
                />
              }
            >
              {COLLEGE_HEADER_STRINGS.buttons.website}
            </Button>
          )}

          <Button
            hierarchy="Primary"
            size="md"
            onClick={handleBookmark}
            startIcon={
              <AppIcon
                iconName={hasBookmarked ? 'BookmarkCheck' : 'Bookmark'}
                diameter="20px"
                color="Colors/Text/text-primary-on-brand"
              />
            }
          >
            {hasBookmarked
              ? COLLEGE_HEADER_STRINGS.buttons.bookmarkBookmarked
              : COLLEGE_HEADER_STRINGS.buttons.bookmarkNotBookmarked}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  mb: {
    xs: backrs.spacing['spacing-3xl'],
    sm: backrs.spacing['spacing-xl'],
  },

  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
});

const largeImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: `${HEADER_IMAGE_HEIGHT}px`,
  '@media (max-height: 768px)': {
    height: `${HEADER_IMAGE_HEIGHT * 0.75}px`,
  },
  '@media (max-width: 414px)': {
    height: `${HEADER_IMAGE_HEIGHT * 0.25}px`,
  },
  '@media (max-width: 900px)': {
    height: `${HEADER_IMAGE_HEIGHT * 0.75}px`,
  },
  objectFit: 'cover',
  objectPosition: 'top',
  backgroundColor: backrs.colors['Warning/400'],
});

const noBannerImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: `${NO_BANNER_IMAGE_HEIGHT}px`,
  '@media (max-height: 768px)': {
    height: `${NO_BANNER_IMAGE_HEIGHT * 0.75}px`,
  },
  '@media (max-width: 414px)': {
    height: `${NO_BANNER_IMAGE_HEIGHT * 0.25}px`,
  },
  '@media (max-width: 900px)': {
    height: `${NO_BANNER_IMAGE_HEIGHT * 0.75}px`,
  },
  objectFit: 'cover',
  objectPosition: 'top',
  backgroundColor: backrs.colors['Warning/400'],
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  borderRight: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
});

const iconImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  maxWidth: `${LOGO_SIZE}px`,
  minWidth: `${LOGO_SIZE}px`,
  maxHeight: `${LOGO_SIZE}px`,
  minHeight: `${LOGO_SIZE}px`,
  objectFit: 'cover',
  borderRadius: '50%',
  position: 'absolute',
  top: {
    xs: `-${LOGO_SIZE * 1.1}px`,
    sm: `-${LOGO_SIZE / 2.25}px`,
  },
  left: {
    xs: backrs.spacing['spacing-3xl'],
    md: backrs.spacing['spacing-4xl'],
  },
  border: `3px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],

  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
});

const schoolInfoSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  maxWidth: '936px !important',
  mt: {
    xs: backrs.spacing['spacing-6xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
  pl: {
    xs: backrs.spacing['spacing-4xl'],
    sm: `calc(${LOGO_SIZE}px + ${backrs.spacing['spacing-5xl']} + ${backrs.spacing['spacing-2xl']})`,
  },
  '@media (min-width: 600px)': {
    pl: `calc(${LOGO_SIZE}px + ${backrs.spacing['spacing-5xl']} + ${backrs.spacing['spacing-2xl']})`,
  },
  '@media (max-width: 414px)': {
    pl: backrs.spacing['spacing-2xl'],
  },

  pr: backrs.spacing['spacing-4xl'],
  width: '100%',
  display: 'flex',
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
});

const schoolNameSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  pb: {
    xs: backrs.spacing['spacing-2xl'],
  },
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});
